import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, MapPin, Users, CalendarDays } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post } from '../../api/client';
import { useEvents } from '../../context/EventContext';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  Card, Button, Modal, Field, Input, Select, Combobox, Textarea, StatusBadge, Badge,
  Loader, EmptyState, SearchInput, SectionTitle, stagger, fadeUp,
} from '../../components/UI';
import { fmtDate, EVENT_TYPE_LABEL } from '../../utils/format';

const TYPES = Object.entries(EVENT_TYPE_LABEL);

/** Applies the server's `requires` rules as the user ticks boxes. */
function applyRules(modules, catalogue) {
  const out = { ...modules };
  catalogue.forEach((m) => { if (m.requires && !out[m.requires]) out[m.key] = false; });
  return out;
}

const BLANK = {
  title: '', type: 'conference', description: '',
  startDate: '', endDate: '',
  corporateId: '', corporateName: '',
  venueName: '', venueCity: '', status: 'draft',
  modules: null,   // filled from the type preset once the catalogue loads
};

export default function Events() {
  const { refresh } = useEvents();
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const nav = useNavigate();
  const { pathname } = useLocation();
  // These pages are mounted under every portal, so links must stay in the
  // portal the user is actually in.
  const base = `/${pathname.split('/').filter(Boolean)[0] || 'admin'}`;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Deactivated clients stay in the database but must not be offered here.
  // Only the companies an event can actually be run for: a group head with
  // divisions under it is left out, since its people sit in one of those.
  const { data: companies } = useFetch('/corporates', { limit: 500, isActive: 'true', selectable: 'true' }, { skip: !isSuperAdmin });
  const { data: catalogue } = useFetch('/events/meta');

  const moduleList = catalogue?.modules || [];
  const presets = catalogue?.modulePresets || {};

  /* The event type seeds the ticks; the organiser adjusts from there. */
  const presetFor = (type) => moduleList.reduce(
    (acc, m) => ({ ...acc, [m.key]: (presets[type] || []).includes(m.key) }), {},
  );
  const modules = form.modules || presetFor(form.type);

  const chooseType = (type) => setForm((f) => ({ ...f, type, modules: presetFor(type) }));
  const toggleModule = (key) => setForm((f) => ({
    ...f,
    modules: applyRules({ ...(f.modules || presetFor(f.type)), [key]: !modules[key] }, moduleList),
  }));

  const { data: events, loading, reload } = useFetch('/events', {
    limit: 60,
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
  });

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Give the event a name';
    if (!form.startDate) e.startDate = 'Start date is required';
    if (!form.endDate) e.endDate = 'End date is required';
    if (form.startDate && form.endDate && new Date(form.endDate) < new Date(form.startDate)) {
      e.endDate = 'End date cannot be before the start date';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const create = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await post('/events', {
        title: form.title.trim(),
        type: form.type,
        description: form.description,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        venue: { name: form.venueName, city: form.venueCity },
        // An existing company goes by id; a typed one goes by name and the
        // server finds-or-creates it.
        ...(form.corporateId
          ? { corporates: [form.corporateId] }
          : form.corporateName.trim() ? { corporateName: form.corporateName.trim() } : {}),
        modules,
      });
      toast.success('Event created');
      setOpen(false);
      setForm(BLANK);
      reload();
      refresh();
      nav(`${base}/events/${res.data._id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionTitle
        title="Events"
        subtitle="Conferences, incentive tours, exhibitions and corporate programmes"
        action={
          <Button icon={Plus} onClick={() => { setForm(BLANK); setErrors({}); setOpen(true); }}>
            New event
          </Button>
        }
      />

      <div style={{ display: 'flex', gap: 11, marginBottom: 18, flexWrap: 'wrap' }}>
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search events…"
          style={{ flex: 1, minWidth: 220 }}
        />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 165 }}>
          <option value="">All statuses</option>
          {['draft', 'published', 'live', 'completed', 'cancelled'].map((s) => (
            <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
          ))}
        </Select>
      </div>

      {loading ? <Loader label="Loading events" /> : !events?.length ? (
        <EmptyState
          message={search || status ? 'No events match your filters' : 'No events yet'}
          hint={search || status ? 'Try a different search or clear the status filter.' : 'Create your first event to begin planning travel, stay and sessions.'}
          icon={CalendarDays}
          action={!search && !status && <Button icon={Plus} onClick={() => setOpen(true)}>Create event</Button>}
        />
      ) : (
        <motion.div
          variants={stagger} initial="hidden" animate="show"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(305px, 1fr))', gap: 15 }}
        >
          {events.map((e) => (
            <motion.div key={e._id} variants={fadeUp}>
              <Link to={`${base}/events/${e._id}`}>
                <Card hover padding={0} style={{ overflow: 'hidden', height: '100%' }}>
                  {/* banner strip */}
                  <div style={{
                    height: 86, position: 'relative',
                    background: e.banner
                      ? `linear-gradient(180deg, rgba(11,11,12,0.15), rgba(11,11,12,0.85)), url(${e.banner}) center/cover`
                      : 'linear-gradient(125deg, #1A1A20 0%, #24242B 50%, #15151A 100%)',
                  }}>
                    {!e.banner && (
                      <motion.div
                        animate={{ opacity: [0.3, 0.62, 0.3] }}
                        transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
                        style={{
                          position: 'absolute', inset: 0,
                          background: 'radial-gradient(320px 90px at 22% 105%, rgba(239,212,138,0.28), transparent 70%)',
                        }}
                      />
                    )}
                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                      <StatusBadge status={e.status} />
                    </div>
                    <div style={{ position: 'absolute', bottom: 11, left: 15 }}>
                      <Badge tone="gold">{EVENT_TYPE_LABEL[e.type] || e.type}</Badge>
                    </div>
                  </div>

                  <div style={{ padding: 17 }}>
                    <h3 style={{ fontSize: 14.8, fontWeight: 600, lineHeight: 1.4, minHeight: 41 }}>{e.title}</h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 12, fontSize: 12.3, color: 'var(--text-mid)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <CalendarDays size={13} color="var(--gold-600)" />
                        {fmtDate(e.startDate)} – {fmtDate(e.endDate)}
                      </span>
                      {(e.venue?.name || e.venue?.city) && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <MapPin size={13} color="var(--gold-600)" />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {e.venue?.name || e.venue?.city}
                          </span>
                        </span>
                      )}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <Users size={13} color="var(--gold-600)" />
                        {e.participantCount ?? 0} participant{e.participantCount === 1 ? '' : 's'}
                      </span>
                    </div>

                    {e.corporates?.length > 0 && (
                      <div style={{ marginTop: 13, paddingTop: 12, borderTop: '1px solid var(--ink-750)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {e.corporates.slice(0, 2).map((c) => (
                          <Badge key={c._id} tone="neutral">{c.name}</Badge>
                        ))}
                        {e.corporates.length > 2 && <Badge tone="neutral">+{e.corporates.length - 2}</Badge>}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* ── create modal ─────────────────────────────────────────── */}
      <Modal
        open={open} onClose={() => setOpen(false)}
        title="Create an event"
        subtitle="You can add sessions, hotels and participants once it exists"
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} loading={saving}>Create event</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Event title" required error={errors.title}>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Annual Sales Conclave 2026"
              autoFocus
            />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Event type" hint="Sets the starting ticks below">
              <Select value={form.type} onChange={(e) => chooseType(e.target.value)}>
                {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="live">Live</option>
              </Select>
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Start date" required error={errors.startDate}>
              <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </Field>
            <Field label="End date" required error={errors.endDate}>
              <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            </Field>
          </div>

          {isSuperAdmin && (
            <Field
              label="Company"
              hint="Divisions and their sub-divisions are listed; a group head is not, because its people belong to one of them. Type a new name to add one."
            >
              <Combobox
                value={form.corporateName}
                placeholder="Search companies, or type a new one…"
                createLabel="Add"
                options={(companies || []).map((c) => ({
                  value: c._id,
                  label: c.name,
                  sub: [c.parent?.name && `under ${c.parent.name}`, c.contactPerson && `${c.contactPerson}${c.contactDesignation ? `, ${c.contactDesignation}` : ''}`]
                    .filter(Boolean).join(' · '),
                }))}
                onChange={(text) => setForm((f) => ({ ...f, corporateName: text }))}
                onPick={(opt) => setForm((f) => ({ ...f, corporateId: opt?.value || '' }))}
              />
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Location">
              <Input value={form.venueName} onChange={(e) => setForm({ ...form, venueName: e.target.value })} placeholder="The Leela Palace" />
            </Field>
            <Field label="City">
              <Input value={form.venueCity} onChange={(e) => setForm({ ...form, venueCity: e.target.value })} placeholder="New Delhi" />
            </Field>
          </div>

          <Field
            label="What this event includes"
            hint="Anything left unticked is hidden from the app, the portals and the API"
          >
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 2,
              border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: 6,
            }}>
              {!moduleList.length && (
                <p style={{ fontSize: 12.5, color: 'var(--text-lo)', padding: 8 }}>Loading modules…</p>
              )}
              {moduleList.map((m) => {
                const parentOff = m.requires && !modules[m.requires];
                return (
                  <label key={m.key} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 9px',
                    borderRadius: 'var(--r-sm)', cursor: parentOff ? 'not-allowed' : 'pointer',
                    opacity: parentOff ? 0.45 : 1,
                    marginLeft: m.requires ? 22 : 0,
                    background: modules[m.key] ? 'rgba(239,212,138,0.08)' : 'transparent',
                  }}>
                    <input
                      type="checkbox"
                      checked={Boolean(modules[m.key])}
                      disabled={parentOff}
                      onChange={() => toggleModule(m.key)}
                      style={{ accentColor: 'var(--gold-400)', marginTop: 2 }}
                    />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 13, display: 'block' }}>{m.label}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-lo)' }}>{m.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Description" hint="Shown to attendees in the mobile app">
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="A short summary of the programme…"
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
